import clsx from 'clsx';
import { memo, type ChangeEvent } from 'react';

import type { RadioProps } from '../types';

import { Layout } from '../../Layout/Layout';
import { Text } from '../../Text/Text';
import './Radio.css';

export const Radio = memo(function Radio({
  label,
  name,
  selected,
  text,
  value,
  onSelectedChange,
  disabled,
  hint,
}: RadioProps) {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onSelectedChange(event.target.checked);
  };

  const inputId = `${name}-${value}`;
  const hintId = hint ? `${inputId}-hint` : undefined;

  return (
    <Layout
      align="start"
      className={clsx('eds-radio', disabled && 'eds-radio--disabled')}
      spacing="xs"
    >
      <span className="eds-radio--control">
        <input
          aria-describedby={hintId}
          aria-label={label}
          checked={selected}
          className="eds-radio--input"
          disabled={disabled}
          id={inputId}
          name={name}
          type="radio"
          value={value}
          onChange={handleChange}
        />
        <span
          aria-hidden="true"
          className={clsx(
            'eds-radio--indicator',
            selected && 'eds-radio--indicator-selected',
          )}
        />
      </span>
      <Layout direction="column" flex="1" spacing="xs">
        <Layout as="label" className="eds-radio--label" htmlFor={inputId}>
          <Text as="span" variant="body-m">
            {text}
          </Text>
        </Layout>
        {hint ? (
          <Text as="p" id={hintId} tone="subdued" variant="body-s">
            {hint}
          </Text>
        ) : null}
      </Layout>
    </Layout>
  );
});

Radio.displayName = 'Radio';
